import React, { useState, useEffect } from 'react';
import { Quote, Star, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import axios from 'axios';

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, [backendUrl]);

  useEffect(() => {
    if (!isAutoPlaying || testimonials.length === 0 || showVideo) return;

    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, testimonials.length, showVideo]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
    setShowVideo(false);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
    setShowVideo(false);
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
    setShowVideo(false);
  };

  if (loading) {
    return (
      <div className="py-20 bg-black flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-silver border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (testimonials.length === 0) return null;

  const current = testimonials[currentTestimonial];

  return (
    <section className="py-20 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-silver/60 text-sm font-mono tracking-widest uppercase mb-4">
            Client Testimonials
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-silver mb-6">
            What Our Clients
            <span className="block gradient-text">Say About Us</span>
          </h2>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Real stories from enterprise leaders who have transformed their businesses with Hara-XY.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-5xl mx-auto">
          <div className="glass-morphism rounded-3xl p-8 md:p-14 relative overflow-hidden min-h-[500px] flex flex-col justify-center border border-white/5">
            {/* Background Quote Icon */}
            {!showVideo && (
              <div className="absolute top-8 right-8 opacity-5">
                <Quote size={180} className="text-silver" />
              </div>
            )}

            {/* Testimonial Content */}
            <div className="relative z-10 transition-all duration-500">
              {showVideo && current.videoUrl ? (
                <div className="rounded-2xl overflow-hidden aspect-video bg-black shadow-2xl ring-1 ring-white/10 animate-in fade-in zoom-in duration-500">
                  <video
                    autoPlay
                    controls
                    className="w-full h-full object-cover"
                    src={`${backendUrl}${current.videoUrl}`}
                  />
                  <button
                    onClick={() => setShowVideo(false)}
                    className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white transition-colors"
                  >
                    Close Video
                  </button>
                </div>
              ) : (
                <>
                  {/* Stars */}
                  <div className="flex justify-center mb-8">
                    {[...Array(current.rating)].map((_, i) => (
                      <Star key={i} size={28} className="text-yellow-400 fill-current mx-1 shadow-glow-sm" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-2xl md:text-3xl text-silver leading-relaxed text-center mb-12 font-light italic px-4 md:px-12">
                    "{current.content}"
                  </blockquote>

                  {/* Video Play Button Overlay */}
                  {current.videoUrl && (
                    <div className="flex justify-center mb-12">
                      <button
                        onClick={() => setShowVideo(true)}
                        className="flex items-center gap-3 px-6 py-3 bg-silver/10 hover:bg-silver/20 border border-silver/30 rounded-full text-silver transition-all duration-300 hover:scale-105"
                      >
                        <Play size={20} fill="currentColor" />
                        <span className="font-medium">Watch Video Testimonial</span>
                      </button>
                    </div>
                  )}

                  {/* Author Info */}
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-silver/20 to-gray-400/10 rounded-full border border-white/10 flex items-center justify-center shadow-inner">
                      <span className="text-silver font-bold text-2xl uppercase">{current.name.charAt(0)}</span>
                    </div>
                    <div className="text-center">
                      <div className="text-silver font-semibold text-xl tracking-tight">{current.name}</div>
                      <div className="text-silver/50 font-medium">{current.company || 'Private Customer'}</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Navigation Arrows */}
            {!showVideo && (
              <>
                <button
                  onClick={prevTestimonial}
                  className="absolute left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group shadow-lg"
                >
                  <ChevronLeft size={28} className="text-silver group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group shadow-lg"
                >
                  <ChevronRight size={28} className="text-silver group-hover:translate-x-0.5 transition-transform" />
                </button>
              </>
            )}
          </div>

          {/* Testimonial Indicators */}
          <div className="flex justify-center mt-12 space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-500 ${index === currentTestimonial
                  ? 'bg-silver w-10'
                  : 'bg-silver/20 hover:bg-silver/40'
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-24 text-center">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="glass-morphism rounded-2xl p-6 border border-white/5">
              <div className="text-3xl font-bold gold-gradient-text mb-2">100%</div>
              <div className="text-gray-400 text-sm font-medium uppercase tracking-widest text-[10px]">Client Satisfaction</div>
            </div>
            <div className="glass-morphism rounded-2xl p-6 border border-white/5">
              <div className="text-3xl font-bold gold-gradient-text mb-2">5.0</div>
              <div className="text-gray-400 text-sm font-medium uppercase tracking-widest text-[10px]">Average Rating</div>
            </div>
            <div className="glass-morphism rounded-2xl p-6 border border-white/5">
              <div className="text-3xl font-bold gold-gradient-text mb-2">95%</div>
              <div className="text-gray-400 text-sm font-medium uppercase tracking-widest text-[10px]">Client Retention</div>
            </div>
            <div className="glass-morphism rounded-2xl p-6 border border-white/5">
              <div className="text-3xl font-bold gold-gradient-text mb-2">24/7</div>
              <div className="text-gray-400 text-sm font-medium uppercase tracking-widest text-[10px]">Dedicated Support</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
